import { useRouter } from 'next/router';
import Cookies from 'js-cookie';

import { DEFAULT_CURRENCY_CODE, GEO_CURRENCY_COOKIE } from '@lib/geo/constants';

export type UseGeoCurrencyResult = {
  /** ISO 4217 code, always uppercase, e.g. `USD`. */
  currencyCode: string;
};

/**
 * Resolves the visitor's display currency (FFH-1746).
 *
 * Precedence:
 * 1. An explicit `?currencyCode=` query param — preserves the manual
 *    test path (`/join?currencyCode=USD`) and any legacy links.
 * 2. The `geo-currency` cookie written by `middleware.ts` from Vercel's
 *    geolocation. The cookie arrives on the same response as the HTML,
 *    so it is readable at hydration with no extra request.
 * 3. GBP.
 *
 * `getPriceForCurrency` lowercases internally and falls back to GBP for
 * currencies missing from a price's `currency_options`, so an unusual
 * code from either source is safe.
 */
export const useGeoCurrency = (): UseGeoCurrencyResult => {
  const { query } = useRouter();

  const currencyCodeParam = Array.isArray(query.currencyCode)
    ? query.currencyCode[0]
    : query.currencyCode;
  if (currencyCodeParam) {
    return { currencyCode: currencyCodeParam.toUpperCase() };
  }

  const cookieCurrencyCode = Cookies.get(GEO_CURRENCY_COOKIE);
  if (cookieCurrencyCode) {
    return { currencyCode: cookieCurrencyCode.toUpperCase() };
  }

  return { currencyCode: DEFAULT_CURRENCY_CODE };
};
